import { Component, OnInit, ViewChild, ChangeDetectorRef, AfterViewInit, TemplateRef, Optional } from '@angular/core'
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SubmissionParams } from '../_models/session';
import { CrmUserComponent } from '../shared';
import { MissingSubmissionsComponent } from './missing.submissions.component';
import { ClientDataHelper } from '../_helpers/client-data-helper';

import { ActivatedRoute, Router } from '@angular/router';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { AuthenticationStateHelperService } from '../_helpers/authentication-state-helper/authentication-state-helper.service';
import { FeatureSwitchService } from '../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../_constants/feature-switch-keys';
import { PlacementService } from '../_services/placementservice';
import { FilterSpec, FilterState } from '../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../shared/filter-ux/screen-filter-ux';
import { DatePreset, DateRange, isDatePreset, resolveDatePreset } from '../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../shared/filter-ux/date-presets.component';
import { ClientSuggestionsService } from '../shared/typeahead/client-suggestions.service';
import { SuggestService } from '../_services/suggest/suggest.service';

/** Missing item types — the grid's Attendance / Session / Progress Report ticks, plus an outstanding ILP. */
export const MISSING_ITEM_TYPE_OPTIONS = [
  { value: 'register', label: 'Register not saved' },
  { value: 'report', label: 'Report not submitted' },
  { value: 'progress', label: 'Progress report outstanding' },
  { value: 'ilp', label: 'ILP outstanding' }
];

/** Whole days since the session date. */
export const DAYS_OVERDUE_OPTIONS = [
  { value: '1-2', label: '1–2 days' },
  { value: '3-7', label: '3–7 days' },
  { value: '7+', label: 'More than 7 days' }
];

@Component({
    selector: 'missing-submissions-page',
    templateUrl: 'missing.submissions.page.html',
    styleUrls: ['missing.submissions.page.css'],
    standalone: false
})

export class MissingSubmissions implements OnInit, AfterViewInit {
  @ViewChild(CrmUserComponent, { static: false }) crmUser;
  @ViewChild(MissingSubmissionsComponent, { static: false }) submissionComp;
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  frmSubmissionSearch: UntypedFormGroup;
  searchResultText: string = "";
  errorMsg: string = "";
  currDate: string = '';
  searchData: string = 'submissionSearch';
  submissionParams: SubmissionParams = null;
  users: any = [];
  expandPanel: boolean = false;
  maxDate: string = formatDate(new Date(), 'yyyy-MM-dd', 'en-GB');

  /** For the template's `*appFeatureSwitch`. */
  readonly featureKeys = FeatureSwitchKeys;
  readonly missingItemTypeOptions = MISSING_ITEM_TYPE_OPTIONS;
  readonly daysOverdueOptions = DAYS_OVERDUE_OPTIONS;
  /**
   * The filters.missing-submissions switch. Off (the default, and on any failure): none of the filters
   * below show and the search sends none of their parameters.
   */
  filtersEnabled: boolean = false;
  /** Selected brands; empty means every brand the user may see. */
  companyIds: number[] = [];
  missingItemTypes: string[] = [];
  daysOverdue: string = null;
  consultantIds: number[] = [];
  /** Consultant options — the Orbit consultant directory (id = dbo.Placements.CoordinatorId). */
  consultants: any[] = [];
  /** The date preset behind Date From / Date To, or null when they were set by hand. */
  datePreset: DatePreset | null = null;
  /** search.autocomplete.missing-submissions: the Client, Tutor and Client Referrer boxes suggest matches. Off, they are plain boxes. */
  autocompleteEnabled: boolean = false;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    from: 'string', to: 'string', preset: 'string', brand: 'number[]', missing: 'string[]', overdue: 'string',
    consultant: 'number[]'
  };
  /** Remembered filters and filter state in the URL; used only while filters.missing-submissions is on. */
  private filterUx: ScreenFilterUx;
  private viewReady: boolean = false;
  private filterUxRestored: boolean = false;
  private urlFiltersApplied: boolean = false;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private clientDataHelper: ClientDataHelper,
    public router: Router,
    private featureHelperService: FeatureHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private featureSwitches: FeatureSwitchService,
    private placementService: PlacementService,
    @Optional() private route: ActivatedRoute = null,
    @Optional() public clientSuggestions: ClientSuggestionsService = null,
    @Optional() public suggest: SuggestService = null
  ) { }
    
  ngOnInit() {
    
    let featureList = new Array<string>();
    featureList.push("MissingSubmissionsView");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "MissingSubmissionsView")[0].enabled) {
      console.error("User does not have the required permissions to view the missing submissions screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
      this.authenticationStateHelper.unauthorizedToken = true;
    }

    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    this.frmSubmissionSearch = this.formBuilder.group({
      startDate: [formatDate(oneWeekAgo, 'yyyy-MM-dd', 'en-GB')],
      endDate: [formatDate(today, 'yyyy-MM-dd', 'en-GB')],
      tutor: null,
      clientReferrer: null,
      client: null,
    });

    this.filterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersMissingSubmissions, MissingSubmissions.filterSpec, this.router, this.route);

    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteMissingSubmissions)
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersMissingSubmissions).subscribe(enabled => {
      this.filtersEnabled = enabled;
      if (enabled) {
        this.placementService.GetCrmUserAccounts().subscribe((users: any[]) => this.consultants = users ?? []);
        this.restoreFilterUx();
      }
    });
  }

  ngAfterViewInit() {
    let clientData = this.clientDataHelper.getData(this.searchData);
    if (this.crmUser != null && clientData != null) {
      this.crmUser.UserData.subscribe({
        next: (n) =>
          this.setClientData()
      });
    }
    this.viewReady = true;
    this.restoreFilterUx();
  }

  /**
   * With the switch on, a shared link's filters win. Otherwise this tab's last search (restored by
   * ngAfterViewInit) stands, and failing that, this user's remembered filters apply. Runs once, when
   * both the switch and the view are ready: the search needs the consultant picker.
   */
  private restoreFilterUx() {
    if (this.filterUxRestored || !this.filtersEnabled || !this.viewReady) {
      return;
    }
    this.filterUxRestored = true;
    const hasTabSearch = this.clientDataHelper.getData(this.searchData) != null;
    const fromUrl = this.filterUx.urlState();
    this.urlFiltersApplied = fromUrl != null;
    const state = fromUrl ?? (hasTabSearch ? null : this.filterUx.rememberedState());
    if (state) {
      this.applyFilterState(state);
      this.searchSubmission();
    }
  }

  onDatePreset(selection: DatePresetSelection) {
    this.datePreset = selection.preset;
    const range = this.clampToToday(selection);
    this.frmSubmissionSearch.patchValue({ startDate: range.from, endDate: range.to });
  }

  /** Clear all: back to the default week, forgotten, and out of the URL. */
  clearAll() {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    this.frmSubmissionSearch.reset({
      startDate: formatDate(oneWeekAgo, 'yyyy-MM-dd', 'en-GB'),
      endDate: formatDate(today, 'yyyy-MM-dd', 'en-GB')
    });
    this.companyIds = [];
    this.missingItemTypes = [];
    this.daysOverdue = null;
    this.consultantIds = [];
    this.datePreset = null;
    this.clientDataHelper.storeData(null, this.searchData);
    this.filterUx.clear();
  }

  /** Date To may not be in the future here, so a preset stops at today. */
  private clampToToday(range: DateRange): DateRange {
    return { from: range.from > this.maxDate ? this.maxDate : range.from, to: range.to > this.maxDate ? this.maxDate : range.to };
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const value = this.frmSubmissionSearch.getRawValue();
    return {
      preset: this.datePreset,
      from: this.datePreset ? null : MissingSubmissions.isoDate(value.startDate),
      to: this.datePreset ? null : MissingSubmissions.isoDate(value.endDate),
      brand: this.companyIds,
      missing: this.missingItemTypes,
      overdue: this.daysOverdue,
      consultant: this.consultantIds
    };
  }

  private applyFilterState(state: FilterState) {
    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? this.clampToToday(resolveDatePreset(this.datePreset)) : null;
    this.frmSubmissionSearch.patchValue({
      startDate: range ? range.from : (state.from ?? this.frmSubmissionSearch.get('startDate').value),
      endDate: range ? range.to : (state.to ?? this.frmSubmissionSearch.get('endDate').value)
    });
    this.companyIds = (state.brand as number[]) ?? [];
    this.missingItemTypes = (state.missing as string[]) ?? [];
    this.daysOverdue = (state.overdue as string) ?? null;
    this.consultantIds = (state.consultant as number[]) ?? [];
  }

  /** The form holds a yyyy-MM-dd string until the user edits the date, then a Date. */
  private static isoDate(value: any): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : formatDate(date, 'yyyy-MM-dd', 'en-GB');
  }

  searchSubmission() {
    this.resetValues();
    if (!this.frmSubmissionSearch.valid) {
      return;
    }
    var stDate = this.frmSubmissionSearch.value.startDate;
    var endDate = this.frmSubmissionSearch.value.endDate;
    if (stDate !== null && endDate !== null) {
      if (stDate > endDate) {
        this.errorMsg = "Start date cannot be later than Date To";
        return;
      }
    }
    if (endDate !== null && endDate > this.maxDate) {
      this.errorMsg = "Date To cannot be in the future";
      return;
    }
    this.submissionParams = this.frmSubmissionSearch.value;
    this.submissionParams.user = this.crmUser.selectedAllUsers ? null : this.crmUser.frmCrmUser.value.user;
    // New parameters only when the switch is on; off, the request is the pre-filter shape.
    if (this.filtersEnabled) {
      this.submissionParams.companyIds = this.companyIds ?? [];
      this.submissionParams.missingItemTypes = this.missingItemTypes ?? [];
      this.submissionParams.daysOverdue = this.daysOverdue;
      this.submissionParams.consultantIds = this.consultantIds ?? [];
    }
    this.currDate = new Date().toString();
    this.clientDataHelper.storeData(this.submissionParams, this.searchData);
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
  }

  validateForm(): boolean {
    var ret = false;
    Object.keys(this.frmSubmissionSearch.controls).forEach(key => {
      if (this.frmSubmissionSearch.controls[key].value !== null) {
        ret = true;
      }
    });
    return ret;
  }

  resetValues() {
    this.searchResultText = '';
    this.errorMsg = '';
  }

  setClientData() {
    let clientData = this.clientDataHelper.getData(this.searchData);
    // A shared link's filters, once applied, win over this tab's last search.
    if (clientData != null && !this.urlFiltersApplied) {
      this.expandPanel = true;
      this.frmSubmissionSearch.get("tutor").setValue(clientData.tutor);
      this.frmSubmissionSearch.get("startDate").setValue(clientData.startDate);
      this.frmSubmissionSearch.get("endDate").setValue(clientData.endDate);
      this.frmSubmissionSearch.get("clientReferrer").setValue(clientData.clientReferrer);
      this.frmSubmissionSearch.get("client").setValue(clientData.client);
      if (this.filtersEnabled) {
        this.companyIds = clientData.companyIds ?? [];
        this.missingItemTypes = clientData.missingItemTypes ?? [];
        this.daysOverdue = clientData.daysOverdue ?? null;
        this.consultantIds = clientData.consultantIds ?? [];
      }
      this.crmUser.setUsers(clientData.user)
      this.searchSubmission();
    }
  }
}
